import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Modal, ModalHeader } from '~/components/overlays';
import { Button, Input } from '~/components/ui';
import { ContactCheckboxList } from '~/components/common/ContactCheckboxList';
import { SellerSelectTable } from '~/components/common/SellerSelectTable';
import { useSaveSellerList, type SellerList } from '~/api/sellerLists';
import { toastGraphQLError } from '~/utils';
import { toggleId } from '~/utils/toggleId';

type SellerListModalProps = {
  sellerList: SellerList | null;
  onClose: () => void;
};

const NAME_MAX_LENGTH = 255;

type SavedContactIdsBySeller = Map<string, Set<string>>;

export function SellerListModal({
  sellerList,
  onClose,
}: Readonly<SellerListModalProps>) {
  const { t } = useTranslation('organizations');
  const {
    id,
    name: savedName = '',
    members: savedMembers = [],
  }: Partial<SellerList> = sellerList ?? {};
  const [name, setName] = useState(savedName);
  const [savedContactIdsBySeller] = useState<SavedContactIdsBySeller>(
    () =>
      new Map(
        savedMembers.map(({ seller, contacts }) => [
          seller.id,
          new Set(contacts.map((contact) => contact.id)),
        ])
      )
  );
  const [contactIdsBySeller, setContactIdsBySeller] = useState(
    savedContactIdsBySeller
  );
  const selectedIds = new Set(contactIdsBySeller.keys());
  const { mutateAsync: saveSellerList, isPending } = useSaveSellerList();
  const toggleSeller = (sellerId: string) =>
    setContactIdsBySeller((previous) => {
      const next = new Map(previous);
      if (!next.delete(sellerId)) next.set(sellerId, new Set());
      return next;
    });

  const toggleContact = (sellerId: string, contactId: string) =>
    setContactIdsBySeller((previous) =>
      new Map(previous).set(
        sellerId,
        toggleId(previous.get(sellerId) ?? new Set(), contactId)
      )
    );

  const save = async () => {
    const members = [...contactIdsBySeller].map(
      ([sellingOrganizationId, contactUserIds]) => ({
        sellingOrganizationId,
        contactUserIds: [...contactUserIds],
        useDefaultContacts:
          contactUserIds.size === 0 &&
          (savedContactIdsBySeller.get(sellingOrganizationId) ?? new Set())
            .size > 0,
      })
    );
    try {
      await saveSellerList({ id, name, members });
      toast.success(t('organizations.sellerLists.saved'));
      onClose();
    } catch (error) {
      toastGraphQLError(error);
    }
  };

  return (
    <Modal isOpen onClose={onClose} canClose={!isPending}>
      <div className="bg-neut-light rounded-lg shadow-xl w-[min(640px,90vw)] h-[min(640px,90vh)] flex flex-col">
        <ModalHeader
          title={
            sellerList
              ? t('organizations.sellerLists.editTitle')
              : t('organizations.sellerLists.createTitle')
          }
          onClose={onClose}
          closeDisabled={isPending}
        />
        <div className="px-6 py-4 flex flex-col gap-2 shrink-0">
          <Input
            label={t('organizations.sellerLists.name')}
            name="name"
            value={name}
            onChange={(_, value) => setName(value)}
            required
            maxLength={NAME_MAX_LENGTH}
          />
          <p className="text-sm text-neut-sec">
            {t('organizations.sellerLists.selectedCount', {
              count: selectedIds.size,
            })}
            {' · '}
            {t('organizations.sellerLists.contactsHint')}
          </p>
        </div>
        <SellerSelectTable
          testId="seller-list"
          labels={{
            organization: t('organizations.sellerLists.seller'),
            searchPlaceholder: t('organizations.sellerLists.searchPlaceholder'),
            searchLabel: t('organizations.sellerLists.searchLabel'),
            loading: t('organizations.sellerLists.sellersLoading'),
            empty: t('organizations.sellerLists.noSellers'),
            noResults: t('organizations.sellerLists.noMatchingSellers'),
            error: t('organizations.sellerLists.sellersError'),
            select: (sellerName) =>
              t('organizations.sellerLists.selectSeller', {
                name: sellerName,
              }),
          }}
          selectedIds={selectedIds}
          onToggle={toggleSeller}
          renderExtra={(organization) =>
            selectedIds.has(organization.id) &&
            organization.sellerContacts.length > 0 && (
              <ul className="flex flex-col">
                <ContactCheckboxList
                  contacts={organization.sellerContacts}
                  selectedContactIds={
                    contactIdsBySeller.get(organization.id) ?? new Set()
                  }
                  onToggle={(contact) =>
                    toggleContact(organization.id, contact.id)
                  }
                  checkboxIdPrefix={`seller-list-contact-${organization.id}`}
                />
              </ul>
            )
          }
        />
        <div className="px-6 py-4 border-t border-neut-border flex justify-end gap-3">
          <Button
            size="sm"
            width="auto"
            variant="accent"
            onClick={onClose}
            disabled={isPending}
          >
            {t('organizations.sellerLists.cancel')}
          </Button>
          <Button
            size="sm"
            width="auto"
            onClick={() => void save()}
            loading={isPending}
            disabled={name.trim() === '' || selectedIds.size === 0}
            data-testid="seller-list-save"
          >
            {t('organizations.sellerLists.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
